// TipTap-Editor für Antworten. [PRÜFEN: …]-Stellen werden hervorgehoben (Bob-Entwürfe).
import { useEffect, type ReactNode } from 'react';
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor as TiptapEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const PRUEFEN = /\[PRÜFEN:[^\]]*\]/g;

// Markiert [PRÜFEN: …] im Text, ohne das Dokument zu verändern
const Pruefen = Extension.create({
  name: 'pruefen',
  addProseMirrorPlugins() {
    const key = new PluginKey('pruefen');
    const deco = (doc: Parameters<typeof DecorationSet.create>[0]) => {
      const out: Decoration[] = [];
      doc.descendants((node, pos) => {
        if (!node.isText || !node.text) return;
        for (const m of node.text.matchAll(PRUEFEN))
          out.push(
            Decoration.inline(pos + (m.index ?? 0), pos + (m.index ?? 0) + m[0].length, {
              class: 'pruef',
            }),
          );
      });
      return DecorationSet.create(doc, out);
    };
    return [
      new Plugin({
        key,
        state: {
          init: (_, s) => deco(s.doc),
          apply: (tr, old) => (tr.docChanged ? deco(tr.doc) : old),
        },
        props: { decorations: (s) => key.getState(s) as DecorationSet },
      }),
    ];
  },
});

export function useMailEditor(
  initialHtml: string,
  onChange: (html: string) => void,
  onSlash?: () => void,
) {
  const editor = useEditor({
    extensions: [
      // Link kommt unten mit eigener Konfiguration (kein Öffnen per Klick, Autolink); Underline steckt im StarterKit
      StarterKit.configure({ heading: false, codeBlock: false, link: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Pruefen,
    ],
    content: initialHtml,
    editorProps: {
      attributes: { class: 'tiptap', 'aria-label': 'Nachrichtentext' },
      // `/` in einem leeren Absatz öffnet die Textbausteine (2.A)
      handleKeyDown: (view, event) => {
        if (event.key !== '/' || !onSlash) return false;
        const { $from } = view.state.selection;
        if ($from.parent.isTextblock && $from.parent.content.size === 0) {
          onSlash();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });
  return editor;
}

// Link setzen/ändern: markierten Text verlinken, ohne Markierung die Adresse als Linktext einfügen
function linkSetzen(editor: TiptapEditor) {
  const alt = (editor.getAttributes('link')['href'] as string | undefined) ?? '';
  const eingabe = window.prompt('Link-Adresse (leer = Link entfernen)', alt || 'https://');
  if (eingabe === null) return;
  const href = eingabe.trim();
  if (!href || href === 'https://') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  const url = /^(https?:|mailto:|tel:)/i.test(href) ? href : 'https://' + href;
  if (editor.state.selection.empty && !editor.isActive('link')) {
    editor
      .chain()
      .focus()
      .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
      .run();
    return;
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
}

export function Formatleiste({ editor }: { editor: TiptapEditor }) {
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      link: e.isActive('link'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
    }),
  });
  const B = ({
    on,
    tip,
    children,
    run,
  }: {
    on: boolean;
    tip: string;
    children: ReactNode;
    run: () => void;
  }) => (
    <button
      type="button"
      className={on ? 'on' : ''}
      title={tip}
      aria-pressed={on}
      onMouseDown={(e) => e.preventDefault()} // Fokus bleibt im Editor
      onClick={run}
    >
      {children}
    </button>
  );
  return (
    <div className="ed-fmt" role="toolbar" aria-label="Formatierung">
      <B on={st.bold} tip="Fett (⌘B)" run={() => editor.chain().focus().toggleBold().run()}>
        <b>B</b>
      </B>
      <B on={st.italic} tip="Kursiv (⌘I)" run={() => editor.chain().focus().toggleItalic().run()}>
        <i>I</i>
      </B>
      <B
        on={st.underline}
        tip="Unterstrichen (⌘U)"
        run={() => editor.chain().focus().toggleUnderline().run()}
      >
        <u>U</u>
      </B>
      <B on={st.link} tip="Link (⌘K im Editor nicht belegt – Knopf nutzen)" run={() => linkSetzen(editor)}>
        🔗
      </B>
      <i className="ed-sep" />
      <B
        on={st.bullet}
        tip="Aufzählung"
        run={() => editor.chain().focus().toggleBulletList().run()}
      >
        •≡
      </B>
      <B
        on={st.ordered}
        tip="Nummerierung"
        run={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1≡
      </B>
      <B
        on={false}
        tip="Formatierung entfernen"
        run={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        ⌫
      </B>
    </div>
  );
}

export function MailEditor({ editor }: { editor: TiptapEditor | null }) {
  useEffect(() => {
    if (editor && !editor.isDestroyed && editor.isEmpty) editor.commands.focus('start');
  }, [editor]);
  return (
    <div className="editor">
      <EditorContent editor={editor} />
    </div>
  );
}

export function countPruefen(html: string): number {
  return (html.match(PRUEFEN) ?? []).length;
}
